import { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2, ScrollText, Lock, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useRun } from "@/context/RunContext";
import { fetchCloseReadiness, approveClose, fetchAuditTrail } from "@/lib/api";

const inr = (v) => `₹${(v ?? 0).toLocaleString("en-IN")}`;

const Kpi = ({ value, label, testid }) => (
  <div data-testid={testid}>
    <p className="font-mono text-3xl text-zinc-900">{value}</p>
    <p className="mt-1 text-[11px] uppercase tracking-wide text-zinc-400">{label}</p>
  </div>
);

export default function CloseReadiness() {
  const { periodId, refreshReadiness } = useRun();
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState(false);
  const [trail, setTrail] = useState(null);
  const [trailOpen, setTrailOpen] = useState(false);

  const load = useCallback(() => {
    fetchCloseReadiness(periodId)
      .then(setD)
      .catch(() => setD({ error: true }));
  }, [periodId]);

  useEffect(() => {
    setD(null);
    load();
  }, [load]);

  const approve = async () => {
    setBusy(true);
    try {
      const res = await approveClose(periodId);
      setD({ ...d, close_approval: res });
      refreshReadiness();
      toast.success(`${d.period} approved for close`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not approve the close");
    } finally {
      setBusy(false);
    }
  };

  const openTrail = async () => {
    setTrailOpen(true);
    setTrail(null);
    fetchAuditTrail(periodId)
      .then((res) => setTrail(res.events))
      .catch(() => setTrail([]));
  };

  if (!d)
    return (
      <p className="flex items-center gap-2 text-sm text-zinc-500">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading close readiness…
      </p>
    );

  if (d.error)
    return <p className="text-sm text-red-600">No close data available for this period.</p>;

  return (
    <div className="max-w-4xl" data-testid="close-readiness-page">
      <div className="flex flex-wrap items-baseline justify-between gap-4 border-b border-zinc-200 pb-6">
        <p className="font-mono text-xs uppercase tracking-[0.18em] text-zinc-400">
          Stage 03 · Close Readiness
        </p>
        <p className="font-mono text-sm text-zinc-500">
          {d.period} {d.close_type} Close
        </p>
      </div>

      <motion.section
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="border-b border-zinc-200 py-14 text-center"
        data-testid="readiness-banner"
      >
        <span
          className={`inline-flex h-11 w-11 items-center justify-center rounded-full border ${
            d.ready
              ? "border-emerald-300 bg-emerald-50 text-emerald-700"
              : "border-amber-300 bg-amber-50 text-amber-700"
          }`}
        >
          {d.ready ? <Check className="h-5 w-5" strokeWidth={3} /> : <Lock className="h-4 w-4" />}
        </span>
        <h1
          className={`mt-5 font-heading text-4xl font-medium tracking-tight sm:text-5xl ${
            d.ready ? "text-zinc-900" : "text-amber-800"
          }`}
          data-testid="readiness-headline"
        >
          {d.headline}
        </h1>
        <p className="mt-3 text-base text-zinc-500 md:text-lg" data-testid="readiness-subline">
          {d.subline}
        </p>

        <div className="mx-auto mt-10 grid max-w-2xl grid-cols-2 gap-8 text-left sm:grid-cols-4">
          <Kpi value={d.kpis.reviewed} label="Reviewed" testid="kpi-reviewed" />
          <Kpi value={d.kpis.auto_reconciled} label="Auto-Reconciled" testid="kpi-auto" />
          <Kpi value={d.kpis.resolved} label="Resolved" testid="kpi-resolved" />
          <Kpi value={d.kpis.blockers} label="Blockers" testid="kpi-blockers" />
        </div>
      </motion.section>

      <section className="border-b border-zinc-200 py-10" data-testid="readiness-checklist">
        <h2 className="font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-400">
          Close readiness check
        </h2>
        <ul className="mt-5 space-y-3">
          {d.checks.map((c) => (
            <li key={c.label} className="flex items-start gap-3 text-sm">
              {c.done ? (
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-emerald-700" strokeWidth={3} />
              ) : (
                <span className="mt-1 h-3 w-3 shrink-0 rounded-full border border-amber-400" />
              )}
              <span className={c.done ? "text-zinc-800" : "text-zinc-400"}>{c.label}</span>
            </li>
          ))}
        </ul>
      </section>

      <div className="grid gap-10 border-b border-zinc-200 py-10 md:grid-cols-2 md:gap-14">
        <section data-testid="ai-close-summary">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-400">
            AI close summary
          </h2>
          <div className="mt-5 space-y-4">
            {d.ai_summary.map((p) => (
              <p key={p} className="text-sm leading-relaxed text-zinc-800">
                {p}
              </p>
            ))}
          </div>
        </section>

        <section data-testid="open-items">
          <h2 className="font-mono text-[11px] uppercase tracking-[0.16em] text-zinc-400">
            Open items
          </h2>
          <p className="mt-5 font-mono text-sm text-zinc-900">
            {d.open_items.length} Open Receivable{d.open_items.length === 1 ? "" : "s"}
          </p>
          <div className="mt-5 space-y-5">
            {d.open_items.map((o) => (
              <div
                key={o.invoice_id}
                className="rounded-md border border-zinc-200 bg-white p-4"
                data-testid={`open-item-${o.invoice_id}`}
              >
                <p className="font-mono text-sm text-zinc-900">{o.invoice_id}</p>
                <p className="mt-0.5 text-sm text-zinc-700">{o.customer}</p>
                <p className="mt-1 font-mono text-sm text-zinc-900">{inr(o.amount)}</p>
                <p className="mt-1 font-mono text-xs text-zinc-500">{o.state}</p>
                <p className="mt-0.5 font-mono text-xs text-zinc-500">
                  Close Blocker:{" "}
                  <span className={o.close_blocker === "Yes" ? "text-amber-700" : "text-zinc-900"}>
                    {o.close_blocker}
                  </span>
                </p>
              </div>
            ))}
          </div>
        </section>
      </div>

      <div className="flex flex-col items-center gap-4 py-12">
        {d.close_approval ? (
          <div className="text-center" data-testid="close-approved">
            <p className="inline-flex items-center gap-2 font-mono text-sm text-emerald-700">
              <CheckCircle2 className="h-4 w-4" /> {d.period} closed by {d.close_approval.approver}
            </p>
          </div>
        ) : (
          <button
            data-testid="approve-close"
            disabled={busy || !d.ready}
            onClick={approve}
            className="inline-flex items-center gap-2 rounded-full bg-zinc-900 px-7 py-3 text-sm font-medium text-white transition-colors duration-200 hover:bg-zinc-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Approve {d.close_type} Close
          </button>
        )}

        <button
          data-testid="view-audit-trail"
          onClick={openTrail}
          className="inline-flex items-center gap-1.5 text-sm text-zinc-500 transition-colors duration-150 hover:text-zinc-900"
        >
          <ScrollText className="h-3.5 w-3.5" /> View Audit Trail
        </button>
      </div>

      <Dialog open={trailOpen} onOpenChange={setTrailOpen}>
        <DialogContent data-testid="audit-trail-modal" className="max-w-2xl border-zinc-200 bg-white">
          <DialogHeader className="text-left">
            <DialogTitle className="font-heading text-lg font-medium tracking-tight text-zinc-900">
              Audit Trail
            </DialogTitle>
            <DialogDescription className="text-sm text-zinc-500">
              {d.period} · every decision recorded during this close
            </DialogDescription>
          </DialogHeader>
          {!trail && (
            <p className="flex items-center gap-2 py-6 text-sm text-zinc-500">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading events…
            </p>
          )}
          {trail && !trail.length && (
            <p className="py-6 text-sm text-zinc-500" data-testid="audit-empty">
              No decisions recorded yet.
            </p>
          )}
          {trail?.length > 0 && (
            <ul className="max-h-[50vh] space-y-4 overflow-y-auto" data-testid="audit-events">
              {trail.map((e) => (
                <li key={`${e.at}-${e.detail}`} className="border-l-2 border-zinc-200 pl-4">
                  <p className="font-mono text-xs text-zinc-400">
                    {new Date(e.at).toLocaleString("en-IN")}
                  </p>
                  <p className="mt-1 text-sm font-medium text-zinc-900">{e.event}</p>
                  <p className="text-sm text-zinc-600">{e.detail}</p>
                  <p className="mt-0.5 font-mono text-xs text-zinc-500">{e.actor}</p>
                </li>
              ))}
            </ul>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
